import Flex from 'components/ui/Flex';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import CreateReturn from './CreateReturn';
import SearchBarcode from './SearchBarcode';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { companiesSelector } from 'store/reducers/companiesSlice';
import { setOrigin, selectTargetInputMode, setTargetInputMode } from 'store/reducers/navigationSlice';
import { ReturnCenterDisplaySettingsResponse } from 'returnCenter-api';
import { MultiToggler } from 'components/ui/Toggler';
import Spinner from 'components/ui/Spinner';
import Text from 'components/ui/Text';
import { userSelector } from 'store/reducers/usersSlice';
import { getFromLocalStorage, persistToLocalStorage, INPUT_MODE_KEY } from 'utils/localStorage';
import { Option } from 'types/Option';
import CustomerOrderSearchByArticle from './CustomerOrderSearchByArticle/CustomerOrderSearchByArticle';
import { clearCustomerSearch, clearSearchOrders } from 'store/reducers/orderSlice';
import useBreakPoints from 'hooks/useBreakPoints';
import { configSelectors, configThunks } from 'store/reducers/configSlice';

type InputMode = 'search' | 'barcode' | 'customer';

const Home = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const bp = useBreakPoints();
    const [inputMode, setInputMode] = useState<InputMode>(getFromLocalStorage<InputMode>(INPUT_MODE_KEY) ?? 'search');

    const siteSettings = useAppSelector(companiesSelector.selectDisplaySettings);
    const loading = useAppSelector(companiesSelector.selectAppSettingsLoading);
    const user = useAppSelector(userSelector.selectUserProfile);
    const targetInputMode = useAppSelector(selectTargetInputMode);
    const currentCompany = useAppSelector(companiesSelector.selectCurrentCompany);
    const settingsAvailable = useAppSelector(configSelectors.selectSettingsAvailable);

    const toggleOptions: Option<InputMode>[] = [
        { value: 'search', label: t('createReturn') },
        { value: 'barcode', label: t('scanBarcodes') },
        { value: 'customer', label: t('searchCustomer') },
    ];

    useEffect(() => {
        dispatch(setOrigin(inputMode));
    }, []);

    useEffect(() => {
        if (
            !currentCompany ||
            settingsAvailable.isAvailable ||
            settingsAvailable.isLoading ||
            settingsAvailable.hasError
        ) {
            return;
        }

        dispatch(configThunks.getReturnCenterSettings());
    }, [dispatch, currentCompany, settingsAvailable]);

    // React to scanner "newReturn" action dispatched from any view
    useEffect(() => {
        if (!targetInputMode) return;
        handleModeChange(targetInputMode);
        dispatch(setTargetInputMode(null));
    }, [targetInputMode]);

    const handleModeChange = (mode: InputMode) => {
        setInputMode(mode);
        persistToLocalStorage(INPUT_MODE_KEY, mode);
        dispatch(clearSearchOrders());
        dispatch(clearCustomerSearch());
        dispatch(setOrigin(mode));
    };

    if (loading) {
        return (
            <Flex $grow={1} column gap="1rem" justify="center" align="center">
                <Spinner />
            </Flex>
        );
    }

    return (
        <Flex
            $grow={1}
            align="center"
            column
            gap="1rem"
            justify="center"
            className={`home-wrapper ${bp.width < 1520 ? 'p-3' : 'pt-3'}`}
        >
            <Title as="h1" fontSize={50} siteSettings={siteSettings ?? undefined}>
                {t('welcome', 'Welcome', { name: `${user?.givenName ?? user.displayName}!` || '' })}
            </Title>
            <Container $grow={1} column>
                <Switcher column siteSettings={siteSettings ?? undefined}>
                    <MultiToggler<InputMode>
                        options={toggleOptions}
                        checked={inputMode}
                        onClick={handleModeChange}
                        size={51}
                        siteSettings={siteSettings ?? undefined}
                    />
                </Switcher>

                <Content className="home-content-wrapper w-100" column $grow={1}>
                    {inputMode === 'search' && <CreateReturn />}
                    {inputMode === 'barcode' && <SearchBarcode />}
                    {inputMode === 'customer' && <CustomerOrderSearchByArticle />}
                </Content>
            </Container>
        </Flex>
    );
};

export default Home;

const Title = styled(Text)<{ siteSettings?: ReturnCenterDisplaySettingsResponse }>`
    color: ${({ theme, siteSettings }) =>
        siteSettings ? `${siteSettings?.accentThemeColor}; ?? ${theme.colors.accent};` : theme.colors.textFaded};
    text-align: center;
`;

const Switcher = styled(Flex)<{ siteSettings?: ReturnCenterDisplaySettingsResponse }>`
    position: relative;
    border-top-right-radius: var(--radius);
    border-top-left-radius: var(--radius);
`;

const Container = styled(Flex)`
    background-color: ${({ theme }) => theme.colors.transparent};
    border-bottom-left-radius: var(--radius);
    border-bottom-right-radius: var(--radius);
`;
const Content = styled(Flex)`
    padding: 2rem;
    background-color: ${({ theme }) => theme.colors.fillWhite};
    border-bottom-left-radius: var(--radius);
    border-bottom-right-radius: var(--radius);
`;
